import React from 'react';
import moment from 'moment';
import {prettyDollars} from 'js/common/string_helper';
import HelpBubble from 'js/components/help_bubble';
import {
    makeReactivatedFlag,
    makeAgeFlag,
    makeCountryFlag,
    makeGenderFlag,
    makeProfileFlag
} from 'js/common/flag_helper';

const {urls} = window.sn;

const {CountryCodes, InviteStatus, InviteStatusMap, ContentApprovalStatus} = window.sn.enums;

const ContentApprovalStatusMap = window.sn.maps.ContentApprovalStatus;

export const PercentComponent = props => {
    const {data} = props;
    return <span>{`${Math.floor(data * 100)}%`}</span>;
};

export const PrettyDollarsComponent = props => {
    const {data} = props;
    return <span>{prettyDollars(data)}</span>;
};

export const PrettyUnixTimeComponent = ({data}) => (
    <span>
        {data
            ? moment(data * 1000)
                  .local()
                  .format('MM-DD-YYYY')
            : '-'}
    </span>
);

export const PrettyDatetimeComponent = props => {
    const {data} = props;
    return (
        <span>
            {moment(data * 1000)
                .local()
                .format('MM/DD/YYYY [-] h:mmA') || data}
        </span>
    );
};

export const PrettyDateComponent = props => {
    const {data} = props;
    return <span>{moment(data).format('MM/DD/YYYY [-] h:mmA') || data}</span>;
};

export const makeFixedNumberComponent = fixed => props => {
    const {data} = props;
    return <span>{Number(data).toFixed(fixed) || data}</span>;
};

export const YesNoLabelComponent = props => {
    const {data} = props;
    const cls = data ? 'label-success' : 'label-danger';
    const message = data ? 'Yes' : 'No';
    return <span className={`label ${cls}`}>{message}</span>;
};

export const UsernameCell = props => {
    const rowData = getRowDataForColumnComponent(props);
    const {lead_id, username, instagram_un, social_un} = rowData;
    const name = username || instagram_un || social_un;
    const internalUrl = urls.viewUserUrl.replace('__ID__', lead_id);
    const internal = lead_id ? (
        <a target="_blank" rel="noopener noreferrer" href={internalUrl}>
            Leads Page
        </a>
    ) : (
        'No Leads Page'
    );

    const externalUrl = name ? `https://www.instagram.com/${name}` : '#';
    const external = name ? (
        <a target="_blank" rel="noopener noreferrer" href={externalUrl}>
            Instagram Page
        </a>
    ) : (
        'No Instagram Page'
    );

    const displayUsername = name;

    return (
        <span>
            {displayUsername}
            <div>{internal}</div>
            <div>{external}</div>
        </span>
    );
};

export const YoutubeUsernameComponent = props => {
    const rowData = getRowDataForColumnComponent(props);
    const {lead_id, username, instagram_un, social_un, social_id} = rowData;
    const name = username || instagram_un || social_un;
    const internalUrl = urls.viewUserUrl.replace('__ID__', lead_id);
    const internal = lead_id ? (
        <a target="_blank" rel="noopener noreferrer" href={internalUrl}>
            Leads Page
        </a>
    ) : (
        'No Leads Page'
    );

    const externalUrl = social_id ? `https://www.youtube.com/channel/${social_id}` : '#';
    const external = name ? (
        <a target="_blank" rel="noopener noreferrer" href={externalUrl}>
            Youtube Channel
        </a>
    ) : (
        'No Youtube Channel'
    );

    return (
        <span>
            {name}
            <div>{internal}</div>
            <div>{external}</div>
        </span>
    );
};

export const FlagsComponent = props => {
    const rowData = getRowDataForColumnComponent(props);
    const reactivatedFlag = makeReactivatedFlag(rowData.approved_posts_count);
    const ageFlag = makeAgeFlag(rowData.age);
    const genderFlag = makeGenderFlag(rowData.gender);
    const countryFlag = makeCountryFlag(rowData.country);
    const profileFlag = makeProfileFlag(rowData.user_profile_id);
    return (
        <div className="invite-flags">
            {reactivatedFlag}
            {ageFlag}
            {genderFlag}
            {countryFlag}
            {profileFlag}
        </div>
    );
};

export const ASLComponent = props => {
    const rowData = getRowDataForColumnComponent(props);
    const location = [rowData.city, rowData.state, CountryCodes[rowData.country]]
        .filter(v => v)
        .join(', ');
    return (
        <div>
            {rowData.age && <div>{rowData.age}</div>}
            {rowData.gender && <div>{rowData.gender}</div>}
            {location && <div>{location}</div>}
        </div>
    );
};

export const CreatorSuccessRateComponent = props => {
    const rowData = getRowDataForColumnComponent(props);
    if (!rowData.approved_invites_count) {
        return <div className="label label-default">N/A</div>;
    }

    let className = null;
    const value = rowData.accepted_posts_count
        ? rowData.accepted_posts_count / rowData.approved_invites_count
        : 0;
    if (value <= 0.5) {
        className = 'label-danger';
    } else if (value > 0.5 && value <= 0.7) {
        className = 'label-warning';
    } else {
        className = 'label-success';
    }

    return (
        <div>
            <div className={`label ${className}`}>
                {rowData.accepted_posts_count ? rowData.accepted_posts_count : 0} /&nbsp;
                {rowData.approved_invites_count}
            </div>
            <HelpBubble
                text="Total Accepted Posts / Total Approved Invites"
                multiline={false}
                inline
                place="right"
            />
        </div>
    );
};

export const InviteStatusComponent = props => {
    const {data: status} = props;
    let statusClass = 'label-default';
    if (status === InviteStatus.ACCEPTED) statusClass = 'label-warning';
    if (status === InviteStatus.APPROVED) statusClass = 'label-success';
    if (status === InviteStatus.REJECTED) statusClass = 'label-danger';
    return <span className={`label ${statusClass}`}>{InviteStatusMap[status]}</span>;
};

export const ContentApprovalStatusComponent = props => {
    const status = props.data;
    let statusClass = 'label-default';
    if (status === ContentApprovalStatus.PENDING) statusClass = 'label-warning';
    if (status === ContentApprovalStatus.APPROVED) statusClass = 'label-success';
    if (status === ContentApprovalStatus.REJECTED) statusClass = 'label-danger';
    return <span className={`label ${statusClass}`}>{ContentApprovalStatusMap[status]}</span>;
};

export const ActionButton = props => {
    const {onClick, buttonText, classes, disabled} = props;
    return (
        <button
            disabled={disabled}
            onClick={e => onClick(e)}
            className={`btn action-button ${classes}`}
        >
            {buttonText}
        </button>
    );
};
/**
 * @typedef {Readonly<any> & Readonly<{ children?: React.ReactNode; }>} CustomRowComponentProps
 * @property {Record<string, any>} data - The filtered row data, which has an
 *   extra `index` property added by the custom datatable component.
 * @property {Record<string, any>} globalData - The entire list of unfiltered
 *   results from the API response.
 */
/**
 * @typedef {Readonly<any> & Readonly<{ children?: React.ReactNode; }>} CustomColumnComponentProps
 * @property {string | number} data - The cell data.
 * @property {Record<string, string | number> & {index: number}} rowData - The
 *   filtered row data, which has an extra `index` property added by the custom
 *   datatable component.
 * @property {Record<string, any>} metadata - The metadata defined by the
 *   external `columns` prop.
 */

/**
 * Griddle tries to render nested objects as additional table columns, but it
 * only works for the first level of recursion. Any deeper objects get passed to
 * react directly, which breaks the renderer. The workaround is to filter out
 * the keys that aren't specified in the `externalColumns` property, add an
 * `index` property to each entry, and use an undocumented `globalData` property
 * to pass the rest of the object to the renderer. This took an entire day to
 * figure out and I want to kill Griddle.
 *
 * @param {CustomRowComponentProps} props
 * @returns {Record<string, any> | null} The unfiltered row data.
 */
export const getRowDataForRowComponent = props => {
    const {data, globalData} = props;
    const {index} = data;
    if (typeof index !== 'number') {
        console.error(`\`index\` not found on props.data:`, data);
        return null;
    }
    const unfilteredRowData = globalData?.[index] ?? null;
    if (unfilteredRowData === null) {
        console.error(`\`${index}\` not in globalData.length: ${globalData?.length ?? null}`);
    }
    return unfilteredRowData;
};

/**
 * This is a workaround similar to `getRowDataForRowComponent`, but column
 * components don't get the `globalData` prop so we have to use `metadata`
 * instead.
 * @param {CustomColumnComponentProps} props
 * @returns {Record<string, any> | null} The unfiltered row data.
 */
export const getRowDataForColumnComponent = props => {
    const {rowData} = props;

    const rawResults = props?.metadata?.rawResults || {};

    const {index} = rowData;
    if (typeof index !== 'number') {
        console.error(`\`index\` not found on props.rowData:`, rowData);
        return null;
    }
    const unfilteredRowData = rawResults?.[index] ?? null;
    if (unfilteredRowData === null) {
        console.error(
            `\`${index}\` not in metadata.rawResults.length: ${rawResults?.length ?? null}`
        );
    }
    return unfilteredRowData;
};



// WEBPACK FOOTER //
// ./common/griddle_column_helper.js